import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import {
  DrawerComponent,
  ModalModel,
} from '../../shared/drawer/drawer.component';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { RouteService } from '../../../service/route.service';
import { GridConfig } from '../../shared/dynamic-grid/grid-model/grid-config.model';
import { DrawerInputConfig } from '../../../models/drawer-model';
import { DynamicGridComponent } from '../../shared/dynamic-grid/dynamic-grid.component';
import { v4 as uuidv4 } from 'uuid';
import { NIL as NIL_UUID } from 'uuid';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import { DRAWER_PLACEHOLDERS } from '../../shared/constants/drawerPlaceHolders';
import { MAX_INT_32 } from '../../shared/constants/values';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';

export interface PMSSchemaIntegrity extends ModalModel {
  id: string | null;
  pmsId: string;
  pmsObjectId: string;
  pmsObjectColumnName: string;
  createdAt: string;
  modifiedAt: string;
  createdBy: string;
  modifiedBy: string;
  isActive: boolean;
  pmsName?: string;
  pmsObjectName?: string;
  pmsVersion?: string;
}

@Component({
  selector: 'app-pms-schema-integrity',
  templateUrl: './pms-schema-integrity.component.html',
  styleUrls: ['./pms-schema-integrity.component.scss'],
})
export class PMSSchemaIntegrityComponent {
  @ViewChild(DrawerComponent) drawerComponent!: DrawerComponent<PMSSchemaIntegrity>;
  @ViewChild(DynamicGridComponent) dynamicGrid!: DynamicGridComponent;

  pmsSchemaIntegrity: PMSSchemaIntegrity = {
    id: null,
    pmsId: NIL_UUID,
    pmsObjectId: NIL_UUID,
    pmsObjectColumnName: '',
    createdAt: '',
    modifiedAt: '',
    createdBy: '',
    modifiedBy: '',
    isActive: true,
    pmsName: DRAWER_PLACEHOLDERS.SELECT_PMS,
    pmsObjectName: DRAWER_PLACEHOLDERS.SELECT_OBJECT,
    toggleUrl: '',
  };

  ngOnInit() {
    this.fetchPMSNameOptions();
  }

  gridConfig: GridConfig = {
    columns: [
      {
        label: 'Active',
        jsonKey: 'isActive',
        dataType: 'toggle',
        isSortable: false,
      },
      { label: 'PMS Name', jsonKey: 'pmsName', dataType: 'string' },
      { label: 'Supported PMS Version', jsonKey: 'pmsVersion', dataType: 'string' },
      { label: 'PMS Object Name', jsonKey: 'pmsObjectName', dataType: 'string' },
      { label: 'Column Name', jsonKey: 'pmsObjectColumnName', dataType: 'string' },
      {
        label: 'Created By',
        jsonKey: 'createdBy',
        dataType: 'string',
        hideOnToggle: true,
      },
      {
        label: 'Created At',
        jsonKey: 'createdAt',
        dataType: 'date',
        hideOnToggle: true,
      },
      {
        label: 'Modified By',
        jsonKey: 'modifiedBy',
        dataType: 'string',
        hideOnToggle: true,
      },
      {
        label: 'Modified At',
        jsonKey: 'modifiedAt',
        dataType: 'date',
        hideOnToggle: true,
      },
    ],
    filters: [
      {
        label: 'PMS Name',
        jsonKey: 'pmsIds',
        type: 'multi',
        options: [],
        width: 'w-50',
      },
    ],

    editable: true,
    deletable: false,
    displayAdd: true,
    exportable: true,
    isToggleDisabled: true,
    defaultFilters: { isActive: true },
    uniqueTableName: 'PMSSchemaIntegrity',
    displayHideAuditColumnsToggle: true,
  };

  drawerInputConfig: DrawerInputConfig[] = [
    {
      label: 'PMS Name',
      jsonKey: 'pmsName',
      input: 'select',
      dataType: 'select',
      foreignKeyProperty: 'pmsId',
      dropDownOptionsURL: `${API_ENDPOINTS.PMS_MASTER}`,
      dropdownJsonKey: 'pmsNameVersion',
      dropdownSortDirection: 'asc',
      isRequired: true,
      dependentDropDownConfig: {
        dependentDropDownKey: 'pmsObjectName',
        dependentDropDownURL: `${API_ENDPOINTS.PMS_OBJECT}`,
        dependentDropDownParam: 'pmsId',
      }
    },
    {
      label: 'PMS Object Name',
      jsonKey: 'pmsObjectName',
      input: 'select',
      dataType: 'select',
      foreignKeyProperty: 'pmsObjectId',
      dropdownJsonKey: 'name',
      dropdownSortDirection: 'asc',
      isRequired: true,
      isDropDownSearchable: true,
      uniqueIdentifier: 'pmsObjectName',
      options: []
    },
    {
      label: 'Column Name',
      jsonKey: 'pmsObjectColumnName',
      input: 'text',
      dataType: 'string',
      isRequired: true,
      minLength: 1,
      maxLength: 500,
      validationRegex: /^[a-zA-Z0-9_]+$/
    },
    {
      label: 'Active',
      jsonKey: 'isActive',
      input: 'toggle',
      dataType: 'boolean'
    },
  ];

  getDataUrl = API_ENDPOINTS.PMS_SCHEMA_INTEGRITY;
  editDataUrl = API_ENDPOINTS.PMS_SCHEMA_INTEGRITY;
  addDataUrl = API_ENDPOINTS.PMS_SCHEMA_INTEGRITY;
  toggleUrl = API_ENDPOINTS.TOGGLE_PMS_SCHEMA_INTEGRITY;
  getPracticesUrl = API_ENDPOINTS.PMS_MASTER;
  sortBy = 'pmsName';
  sortOrder = 'asc';

  private routerSubscription: Subscription;
  routes: string[] = [];
  
  constructor(
    private routeService: RouteService,
    private cdr: ChangeDetectorRef,
    private _http: HttpClient
  ) { }

  public get http(): HttpClient {
    return this._http;
  }
  public set http(value: HttpClient) {
    this._http = value;
  }

  ngAfterViewInit(): void {
    this.routerSubscription = this.routeService
      .getRoutes()
      .subscribe((data) => {
        this.routes = data;
        this.routes.shift();
        if (this.routes.length == 2) {
          this.routeService.redirectTO(
            this.routes.join('/').concat('/pms-schema-integrity')
          );
        }
        this.cdr.detectChanges();
      });
  }

  onAddHandler(): void {
    if (this.drawerComponent) {
      this.drawerComponent.drawerType = 'add';
      this.drawerInputConfig[0].isDisabled = false;
      this.drawerInputConfig[1].isDisabled = false;
      this.drawerInputConfig[2].isDisabled = false;
      this.resetEntry();
      this.drawerComponent.openDrawer();
    } else {
      console.error('Drawer component is not initialized');
    }
  }

  fetchPMSNameOptions() {
    this._http
      .get<any>(this.getPracticesUrl, { params: { pageSize: MAX_INT_32 } })
      .subscribe((response) => {
        response.data = response.data
          .filter((option) => option.isActive)
          .map((option) => ({
            ...option,
            name: `${option.name} ${option.version}`,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        response.data = [{ id: 'All', name: 'All PMS', checked: true }, ...response.data];
        this.gridConfig.filters.find(
          (filter) => filter.jsonKey === 'pmsIds'
        ).options = response;
      });
  }

  resetEntry() {
    this.drawerComponent.drawerModel = {
      id: null,
      pmsId: NIL_UUID,
      pmsObjectId: NIL_UUID,
      pmsObjectColumnName: '',
      createdAt: '',
      modifiedAt: '',
      createdBy: '',
      modifiedBy: '',
      pmsName: DRAWER_PLACEHOLDERS.SELECT_PMS,
      pmsObjectName: DRAWER_PLACEHOLDERS.SELECT_OBJECT,
      isActive: true,
    };
  }

  onEditHandler(rowData: any): void {
    if (this.drawerComponent) {
      this.drawerComponent.drawerType = 'edit';
      this.drawerInputConfig[0].isDisabled = true;
      this.drawerInputConfig[1].isDisabled = true;
      this.drawerInputConfig[2].isDisabled = true;
      this.drawerComponent.drawerModel = rowData;
      this.drawerComponent.openDrawer();
      this.cdr.detectChanges();
    } else {
      console.error('Drawer component is not initialized');
    }
  }

  refreshHandler() {
    this.dynamicGrid.fetchData();
  }

  @ViewChild(ToastComponent) toastComponent!: ToastComponent;

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
    this.refreshHandler();
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }
}